<script setup lang="ts">
import type { AdminMetricItem } from '../../types'

withDefaults(defineProps<{
  metrics: AdminMetricItem[]
  compact?: boolean
}>(), {
  compact: false
})
</script>

<template>
  <dl class="rr-metric-strip" :class="{ 'is-compact': compact }">
    <div v-for="metric in metrics" :key="metric.key" :class="`is-${metric.tone ?? 'neutral'}`">
      <dt>{{ metric.label }}</dt>
      <dd>{{ metric.value }}</dd>
      <small v-if="metric.hint">{{ metric.hint }}</small>
    </div>
  </dl>
</template>

<style scoped>
.rr-metric-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  background: var(--rr-color-surface);
  box-shadow: var(--rr-shadow-panel);
}

.rr-metric-strip > div {
  position: relative;
  display: grid;
  min-width: 0;
  gap: 4px;
  padding: 14px 16px;
}

.rr-metric-strip > div + div {
  border-left: 1px solid var(--rr-color-border);
}

.rr-metric-strip > div::before {
  position: absolute;
  top: 14px;
  bottom: 14px;
  left: 0;
  width: 2px;
  background: transparent;
  content: "";
}

.rr-metric-strip > .is-primary::before { background: var(--rr-color-primary); }
.rr-metric-strip > .is-success::before { background: var(--rr-color-success); }
.rr-metric-strip > .is-warning::before { background: var(--rr-color-warning); }
.rr-metric-strip > .is-danger::before { background: var(--rr-color-danger); }

.rr-metric-strip dt,
.rr-metric-strip dd {
  margin: 0;
}

.rr-metric-strip dt,
.rr-metric-strip small {
  overflow: hidden;
  color: var(--rr-color-text-muted);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rr-metric-strip dt {
  font-weight: 680;
}

.rr-metric-strip dd {
  color: var(--rr-color-text-strong);
  font-size: 23px;
  font-variant-numeric: tabular-nums;
  font-weight: 760;
  line-height: 1.12;
}

.rr-metric-strip.is-compact > div {
  padding: 11px 14px;
}

.rr-metric-strip.is-compact dd {
  font-size: 19px;
}

@media (max-width: 720px) {
  .rr-metric-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .rr-metric-strip > div:nth-child(odd) {
    border-left: 0;
  }

  .rr-metric-strip > div:nth-child(n + 3) {
    border-top: 1px solid var(--rr-color-border);
  }
}
</style>
